<template>
    <div class="iyb-breakdown">
  
      <!-- EUDR Risk Status Header -->
      <div v-if="!hideHeader" class="d-flex align-center justify-space-between mb-4">
        <div>
          <div class="text-caption text--secondary mb-1">{{ $t('dueDiligence.iyb.regencyKabupaten') }}</div>
          <div class="font-weight-bold text-body-1">{{ regencyName }}</div>
        </div>
        <div class="text-right">
          <div class="text-caption text--secondary mb-1">{{ $t('dueDiligence.iyb.eudrRiskStatus') }}</div>
          <v-btn small rounded depressed class="iyb-risk-btn" :class="getRiskClass(eudrRiskStatus)" min-width="130" height="32">
            <v-icon small class="mr-1">{{ getRiskIcon(eudrRiskStatus) }}</v-icon>
            {{ translateIybRiskLabel(eudrRiskStatus, (key) => $t(key)) || $t('dueDiligence.iyb.notAvailable') }}
          </v-btn>
        </div>
      </div>
  
      <v-divider v-if="!hideHeader" class="mb-4" />
  
      <!-- Environmental Dimension (Indicators 1-4) -->
      <div class="dimension-section mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="#489E16" small class="mr-2">mdi-leaf</v-icon>
          <span class="font-weight-bold text-body-2 text-uppercase" style="color:#489E16; letter-spacing:0.08em;">
            {{ $t('dueDiligence.iyb.environmental') }}
          </span>
        </div>
        <v-row dense>
          <v-col cols="12" sm="6" v-for="ind in environmentalIndicators" :key="ind.key">
            <div class="indicator-card pa-3 mb-2" style="border:1px solid #e8e8e8; border-radius:8px; background:#fafafa;">
              <div class="d-flex justify-space-between align-start">
                <div style="flex:1; padding-right:8px;">
                  <div class="text-caption font-weight-medium" style="line-height:1.3;">{{ ind.name }}</div>
                  <div class="text-caption text--secondary mt-1">
                    <strong>{{ formatVal(ind.value) }}</strong>
                    <span v-if="ind.value !== null && ind.value !== undefined"> / 100</span>
                  </div>
                </div>
                <v-chip small :class="['indicator-risk-badge', ind.badgeClass]">
                  {{ ind.label }}
                </v-chip>
              </div>
              <template v-if="ind.value !== null && ind.value !== undefined">
                <div class="mt-2 indicator-bar-track">
                  <div class="indicator-bar-zone indicator-bar-zone--high"></div>
                  <div class="indicator-bar-zone indicator-bar-zone--medium"></div>
                  <div class="indicator-bar-zone indicator-bar-zone--low"></div>
                  <div class="indicator-bar-fill" :style="{ background: ind.barColor, width: ind.value + '%' }"></div>
                  <div class="indicator-bar-tick indicator-bar-tick--40"></div>
                  <div class="indicator-bar-tick indicator-bar-tick--60"></div>
                </div>
                <div class="indicator-bar-labels">
                  <span>0</span>
                  <span class="indicator-bar-labels__mid">40</span>
                  <span class="indicator-bar-labels__mid">60</span>
                  <span>100</span>
                </div>
              </template>
            </div>
          </v-col>
        </v-row>
      </div>
  
      <!-- Social / Agricultural Dimension (Indicators 5-10) -->
      <div class="dimension-section mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="#FFA826" small class="mr-2">mdi-account-group</v-icon>
          <span class="font-weight-bold text-body-2 text-uppercase" style="color:#FFA826; letter-spacing:0.08em;">
            {{ $t('dueDiligence.iyb.socialAgricultural') }}
          </span>
        </div>
        <v-row dense>
          <v-col cols="12" sm="6" v-for="ind in socialIndicators" :key="ind.key">
            <div class="indicator-card pa-3 mb-2" style="border:1px solid #e8e8e8; border-radius:8px; background:#fafafa;">
              <div class="d-flex justify-space-between align-start">
                <div style="flex:1; padding-right:8px;">
                  <div class="text-caption font-weight-medium" style="line-height:1.3;">{{ ind.name }}</div>
                  <div class="text-caption text--secondary mt-1">
                    <strong>{{ formatVal(ind.value) }}</strong>
                    <span v-if="ind.value !== null && ind.value !== undefined"> / 100</span>
                  </div>
                </div>
                <v-chip small :class="['indicator-risk-badge', ind.badgeClass]">
                  {{ ind.label }}
                </v-chip>
              </div>
              <template v-if="ind.value !== null && ind.value !== undefined">
                <div class="mt-2 indicator-bar-track">
                  <div class="indicator-bar-zone indicator-bar-zone--high"></div>
                  <div class="indicator-bar-zone indicator-bar-zone--medium"></div>
                  <div class="indicator-bar-zone indicator-bar-zone--low"></div>
                  <div class="indicator-bar-fill" :style="{ background: ind.barColor, width: ind.value + '%' }"></div>
                  <div class="indicator-bar-tick indicator-bar-tick--40"></div>
                  <div class="indicator-bar-tick indicator-bar-tick--60"></div>
                </div>
                <div class="indicator-bar-labels">
                  <span>0</span>
                  <span class="indicator-bar-labels__mid">40</span>
                  <span class="indicator-bar-labels__mid">60</span>
                  <span>100</span>
                </div>
              </template>
            </div>
          </v-col>
        </v-row>
      </div>
  
      <!-- Governance Dimension (Indicators 11-13) -->
      <div class="dimension-section">
        <div class="d-flex align-center mb-3">
          <v-icon color="#002287" small class="mr-2">mdi-shield-check</v-icon>
          <span class="font-weight-bold text-body-2 text-uppercase" style="color:#002287; letter-spacing:0.08em;">
            {{ $t('dueDiligence.iyb.governance') }}
          </span>
        </div>
        <v-row dense>
          <v-col cols="12" sm="6" v-for="ind in governanceIndicators" :key="ind.key">
            <div class="indicator-card pa-3 mb-2" style="border:1px solid #e8e8e8; border-radius:8px; background:#fafafa;">
              <div class="d-flex justify-space-between align-start">
                <div style="flex:1; padding-right:8px;">
                  <div class="text-caption font-weight-medium" style="line-height:1.3;">{{ ind.name }}</div>
                  <div class="text-caption text--secondary mt-1">
                    <strong>{{ formatVal(ind.value) }}</strong>
                    <span v-if="ind.value !== null && ind.value !== undefined"> / 100</span>
                  </div>
                </div>
                <v-chip small :class="['indicator-risk-badge', ind.badgeClass]">
                  {{ ind.label }}
                </v-chip>
              </div>
              <template v-if="ind.value !== null && ind.value !== undefined">
                <div class="mt-2 indicator-bar-track">
                  <div class="indicator-bar-zone indicator-bar-zone--high"></div>
                  <div class="indicator-bar-zone indicator-bar-zone--medium"></div>
                  <div class="indicator-bar-zone indicator-bar-zone--low"></div>
                  <div class="indicator-bar-fill" :style="{ background: ind.barColor, width: ind.value + '%' }"></div>
                  <div class="indicator-bar-tick indicator-bar-tick--40"></div>
                  <div class="indicator-bar-tick indicator-bar-tick--60"></div>
                </div>
                <div class="indicator-bar-labels">
                  <span>0</span>
                  <span class="indicator-bar-labels__mid">40</span>
                  <span class="indicator-bar-labels__mid">60</span>
                  <span>100</span>
                </div>
              </template>
            </div>
          </v-col>
        </v-row>
      </div>
  
      <v-divider class="mt-4 mb-3" />
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px;">
        <div class="d-flex align-center" style="gap:12px;">
          <span class="text-caption font-weight-medium text--secondary">{{ $t('dueDiligence.iyb.scoreRanges') }}</span>
          <span class="text-caption" style="color:#489E16;">
            <v-icon x-small style="color:#489E16;">mdi-square</v-icon> {{ $t('dueDiligence.iyb.lowRiskRange') }}
          </span>
          <span class="text-caption" style="color:#FFA826;">
            <v-icon x-small style="color:#FFA826;">mdi-square</v-icon> {{ $t('dueDiligence.iyb.mediumRiskRange') }}
          </span>
          <span class="text-caption" style="color:#DA3D3D;">
            <v-icon x-small style="color:#DA3D3D;">mdi-square</v-icon> {{ $t('dueDiligence.iyb.highRiskRange') }}
          </span>
        </div>
        <div class="text-caption text--secondary">
          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
          {{ dataYear }} &nbsp;|&nbsp;
          <v-icon x-small class="mr-1">mdi-update</v-icon>
          {{ importedAt }}
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import {
    classifyIybIndicatorValue,
    getIybRiskBadgeClass,
    getRiskIconForStatus,
    normalizeRiskStatusLabel,
    translateIybRiskLabel,
  } from '@/utils/iybRiskClassification'
  
  export default {
    name: 'IYBIndicatorBreakdown',
    props: {
      regencyName:    { type: String, default: '' },
      eudrRiskStatus: { type: String, default: null },
      dataYear:       { type: [String, Number], default: '' },
      importedAt:     { type: String, default: '' },
      indicators: {
        type: Object,
        default: () => ({})
      },
      hideHeader: { type: Boolean, default: false },
    },
    computed: {
      ind() {
        return this.indicators || {}
      },
      // IYBv2 BOGOR Indicators 1–4 (0-100 scale each)
      environmentalIndicators() {
        return [
          { key: 'ind1', name: this.$t('dueDiligence.iyb.indicators.spatialPlanning'), value: this.ind.ind1_spatialPlanning,  ...this.classify(this.ind.ind1_spatialPlanning) },
          { key: 'ind2', name: this.$t('dueDiligence.iyb.indicators.forestLandFires'), value: this.ind.ind2_forestLandFires,  ...this.classify(this.ind.ind2_forestLandFires) },
          { key: 'ind3', name: this.$t('dueDiligence.iyb.indicators.waterWaste'), value: this.ind.ind3_waterWaste, ...this.classify(this.ind.ind3_waterWaste) },
          { key: 'ind4', name: this.$t('dueDiligence.iyb.indicators.climateAction'), value: this.ind.ind4_climateAction, ...this.classify(this.ind.ind4_climateAction) },
        ]
      },
      // IYBv2 BOGOR Indicators 5–10
      socialIndicators() {
        return [
          { key: 'ind5',  name: this.$t('dueDiligence.iyb.indicators.landExploitation'), value: this.ind.ind5_landExploitation, ...this.classify(this.ind.ind5_landExploitation) },
          { key: 'ind6',  name: this.$t('dueDiligence.iyb.indicators.independentSmallholders'), value: this.ind.ind6_indSmallholders, ...this.classify(this.ind.ind6_indSmallholders) },
          { key: 'ind7',  name: this.$t('dueDiligence.iyb.indicators.farmerInstitutions'), value: this.ind.ind7_farmerInstitutions, ...this.classify(this.ind.ind7_farmerInstitutions) },
          { key: 'ind8',  name: this.$t('dueDiligence.iyb.indicators.businessPartnerships'), value: this.ind.ind8_businessPartnerships, ...this.classify(this.ind.ind8_businessPartnerships) },
          { key: 'ind9',  name: this.$t('dueDiligence.iyb.indicators.indigenousCommunities'), value: this.ind.ind9_indigenousCommunities, ...this.classify(this.ind.ind9_indigenousCommunities) },
          { key: 'ind10', name: this.$t('dueDiligence.iyb.indicators.gender'), value: this.ind.ind10_gender, ...this.classify(this.ind.ind10_gender) },
        ]
      },
      // IYBv2 BOGOR Indicators 11–13
      governanceIndicators() {
        return [
          { key: 'ind11', name: this.$t('dueDiligence.iyb.indicators.spbeInformation'), value: this.ind.ind11_spbeInformation, ...this.classify(this.ind.ind11_spbeInformation) },
          { key: 'ind12', name: this.$t('dueDiligence.iyb.indicators.sp4nLaporComplaints'), value: this.ind.ind12_sp4nlapor, ...this.classify(this.ind.ind12_sp4nlapor) },
          { key: 'ind13', name: this.$t('dueDiligence.iyb.indicators.fpicSop'), value: this.ind.ind13_fpicSop, ...this.classify(this.ind.ind13_fpicSop) },
        ]
      }
    },
    methods: {
      classify(val) {
        return classifyIybIndicatorValue(val, (key) => this.$t(key))
      },
      normalizeRiskStatusLabel,
      translateIybRiskLabel,
      formatVal(val) {
        if (val === null || val === undefined) return this.$t('dueDiligence.iyb.notAvailable')
        return parseFloat(val).toFixed(1)
      },
      getRiskClass(status) {
        return getIybRiskBadgeClass(status)
      },
      getRiskIcon(status) {
        return getRiskIconForStatus(status)
      },
    }
  }
  </script>
  
  <style scoped>
  .indicator-risk-badge {
    min-width: 100px !important;
    height: 32px !important;
    font-size: 14px !important;
    font-weight: 600;
    justify-content: center;
    padding: 0 12px !important;
  }

  .indicator-risk-badge ::v-deep .v-chip__content {
    font-size: 14px !important;
    line-height: 1.2;
  }

  .indicator-bar-track {
    position: relative;
    height: 4px;
    border-radius: 2px;
  }
  .indicator-bar-zone {
    position: absolute;
    top: 0;
    height: 100%;
  }
  .indicator-bar-zone--high {
    left: 0;
    width: 40%;
    background: #FFCDD2;
    border-radius: 2px 0 0 2px;
  }
  .indicator-bar-zone--medium {
    left: 40%;
    width: 20%;
    background: #FFE0B2;
  }
  .indicator-bar-zone--low {
    left: 60%;
    width: 40%;
    background: #C8E6C9;
    border-radius: 0 2px 2px 0;
  }
  .indicator-bar-fill {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    border-radius: 2px;
    z-index: 1;
  }
  .indicator-bar-tick {
    position: absolute;
    top: -3px;
    width: 1px;
    height: 10px;
    background: rgba(0, 0, 0, 0.2);
    z-index: 2;
  }
  .indicator-bar-tick--40 { left: 40%; }
  .indicator-bar-tick--60 { left: 60%; }
  .indicator-bar-labels {
    position: relative;
    height: 12px;
    margin-top: 1px;
  }
  .indicator-bar-labels span {
    position: absolute;
    font-size: 9px;
    color: #9E9E9E;
  }
  .indicator-bar-labels span:first-child {
    left: 0;
    color: #DA3D3D;
  }
  .indicator-bar-labels span:last-child {
    right: 0;
    color: #489E16;
  }
  .indicator-bar-labels__mid {
    transform: translateX(-50%);
  }
  .indicator-bar-labels span:nth-child(2) { left: 40%; }
  .indicator-bar-labels span:nth-child(3) { left: 60%; }

  .iyb-risk-btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
    font-size: 14px !important;
  }

  .iyb-risk-btn >>> .v-btn__content {
    font-size: 14px !important;
  }

  .iyb-badge--green {
    background-color: #e8f5e9 !important;
    border: 2px solid #2e7d32 !important;
    color: #1b5e20 !important;
  }

  .iyb-badge--yellow {
    background-color: #fff8e1 !important;
    border: 2px solid #FFA826 !important;
    color: #E65100 !important;
  }

  .iyb-badge--red {
    background-color: #ffebee !important;
    border: 2px solid #c62828 !important;
    color: #b71c1c !important;
  }

  .iyb-risk-btn.iyb-badge--green >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--yellow >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--red >>> .v-btn__content {
    color: inherit !important;
  }
  </style>
  